import { useEffect, useRef, useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import GlobalLoader from "../common/GlobalLoader";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
} from "firebase/auth";
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from "../../firebase/firebaseConfig";
import { serverTimestamp, setDoc, doc, getDoc } from "firebase/firestore";
import { upsertAccountSubscription } from "../../services/subscriptions";
import { useTranslation } from "react-i18next";
import { Capacitor } from '@capacitor/core';
import { useGoogleAuth } from "../../hooks/useGoogleAuth";

interface SignUpFormProps {
  banner?: React.ReactNode;
}

export default function SignUpForm({ banner }: SignUpFormProps = {}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState<'es' | 'en' | 'pt'>(() => {
    const stored = localStorage.getItem('language');
    const base = (stored || i18n.language || 'es').split('-')[0];
    if (base === 'en' || base === 'pt' || base === 'es') return base;
    return 'es';
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const fromUrl = (urlParams.get("ref") || urlParams.get("referral") || "").trim().toUpperCase();
      if (fromUrl) return fromUrl;
      return (localStorage.getItem("pending_referral_code") || "").trim().toUpperCase();
    } catch {
      return "";
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper para aplicar código de referido después de crear usuario
  const tryApplyReferral = async (targetCode?: string) => {
    const codeToApply = (targetCode || referralCode || localStorage.getItem("pending_referral_code") || "").trim().toUpperCase();
    if (!codeToApply) return;
    try {
      const applyFn = httpsCallable(functions, "applyReferralCode");
      await applyFn({ code: codeToApply });
      localStorage.removeItem("pending_referral_code");
    } catch (e: any) {
      console.warn("[SignUpForm] Could not auto-apply referral code:", e?.message);
    }
  };
  
  // Inicializar Google Auth para móvil
  const { isReady: googleAuthReady } = useGoogleAuth();
  const googleAuthReadyRef = useRef(googleAuthReady);
  useEffect(() => {
    googleAuthReadyRef.current = googleAuthReady;
  }, [googleAuthReady]);

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let tId: any;
    const timeoutPromise = new Promise<T>((_, reject) => {
      tId = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      clearTimeout(tId);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';
      let uid: string;

      if (isNative) {
        if (platform !== 'ios') {
          throw new Error('Apple Sign-In is only supported on iOS in this build.');
        }

        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await withTimeout(
          FirebaseAuthentication.signInWithApple({ scopes: ['email', 'name'] }),
          120000,
          'FirebaseAuthentication.signInWithApple'
        );

        uid = String(result?.user?.uid || '').trim();
        if (!uid) {
          const current = await FirebaseAuthentication.getCurrentUser();
          uid = String(current?.user?.uid || '').trim();
        }
        if (!uid) {
          throw new Error('No native user returned from FirebaseAuthentication.signInWithApple');
        }

        await new Promise((r) => setTimeout(r, 600));
        const tokenRes = await withTimeout(
          FirebaseAuthentication.getIdToken({ forceRefresh: true }),
          30000,
          'FirebaseAuthentication.getIdToken'
        );
        const nativeIdToken = String((tokenRes as any)?.token || '').trim();
        if (!nativeIdToken) {
          throw new Error('No ID token returned from FirebaseAuthentication.getIdToken');
        }

        const exchange = httpsCallable(functions, 'exchangeIdTokenForCustomToken');
        const resp: any = await withTimeout(
          exchange({ idToken: nativeIdToken }),
          30000,
          'exchangeIdTokenForCustomToken'
        );
        const customToken = String(resp?.data?.customToken || '').trim();
        if (!customToken) {
          throw new Error('No custom token returned from exchangeIdTokenForCustomToken');
        }

        const jsCred = await withTimeout(
          signInWithCustomToken(auth, customToken),
          30000,
          'signInWithCustomToken'
        );
        uid = jsCred.user.uid;
      } else {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const cred = await signInWithPopup(auth, provider);
        uid = cred.user.uid;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error(t('forms.usuarionodisponibledespuesdeautenticar'));
      }

      const userDocRef = doc(db, 'users', uid);
      const userSnapshot = await getDoc(userDocRef);
      const existingUser = userSnapshot.exists() ? userSnapshot.data() : null;

      if (existingUser && (existingUser as any).onboardingCompleted) {
        setError(t('auth.alreadyRegistered'));
        setTimeout(() => {
          navigate('/signin', {
            replace: true,
            state: { notice: t('auth.accountExistsSignIn') },
          });
        }, 2000);
        return;
      }

      if (!existingUser) {
        try {
          try {
            await i18n.changeLanguage(selectedLanguage);
          } catch {}
          await setDoc(
            userDocRef,
            {
              uid,
              email: currentUser.email,
              name: currentUser.displayName || null,
              role: 'admin',
              isOwner: true,
              isAdmin: true,
              language: selectedLanguage,
              createdAt: serverTimestamp(),
              accountId: uid,
            },
            { merge: true }
          );
          await upsertAccountSubscription({ ownerUserId: uid, plan: 'free' });
          await tryApplyReferral();
        } catch {
        }
      }

      sessionStorage.setItem('redirecting_to_onboarding', 'true');
      navigate('/onboarding/welcome', { replace: true });
    } catch (e: any) {
      if (
        e?.code === 'auth/popup-closed-by-user' ||
        e?.code === 'auth/cancelled-popup-request'
      ) {
        setError(null);
      } else {
        setError(t('auth.appleSignInFailed', { defaultValue: "No se pudo iniciar sesión con Apple" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const waitForGoogleAuthReady = async (ms: number) => {
    const end = Date.now() + ms;
    while (Date.now() < end) {
      if (googleAuthReadyRef.current) return;
      await new Promise((r) => setTimeout(r, 150));
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';
      let uid: string;
      
      if (isNative) {
        // Android/iOS
        if (platform === 'ios') {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          const result = await withTimeout(FirebaseAuthentication.signInWithGoogle(), 30000, 'FirebaseAuthentication.signInWithGoogle');
          const idToken = String(result?.credential?.idToken || '').trim();
          if (!idToken) {
            throw new Error('No se pudo obtener el token de Google');
          }

          const credential = GoogleAuthProvider.credential(idToken);
          const webResult = await withTimeout(signInWithCredential(auth, credential), 30000, 'signInWithCredential');
          uid = webResult.user.uid;
        } else {
          console.log('[SignUpForm] Using native Google Auth plugin (Android)');
          console.log('[SignUpForm] Google Auth ready:', googleAuthReady);

          if (!googleAuthReadyRef.current) {
            await waitForGoogleAuthReady(4000);
          }
          if (!googleAuthReadyRef.current) {
            throw new Error('Google Auth plugin no está inicializado. Espera un momento y vuelve a intentar.');
          }

          const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

          console.log('[SignUpForm] Calling GoogleAuth.signIn()...');
          let googleUser: any = null;
          try {
            googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn');
          } catch (err: any) {
            const msg = String(err?.message || '');
            if (msg.includes('Timeout: GoogleAuth.signIn')) {
              try {
                await GoogleAuth.signOut();
              } catch {
              }
              await new Promise((r) => setTimeout(r, 250));
              googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn (retry)');
            } else {
              throw err;
            }
          }
          console.log('[SignUpForm] ✅ GoogleAuth.signIn() returned:', googleUser);

          if (!googleUser || !googleUser.authentication || !googleUser.authentication.idToken) {
            console.error('[SignUpForm] ❌ Google user or idToken is missing:', googleUser);
            throw new Error('No se pudo obtener el token de Google');
          }

          const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
          const webResult = await withTimeout(signInWithCredential(auth, credential), 30000, 'signInWithCredential');
          uid = webResult.user.uid;
          console.log('[SignUpForm] ✅ Native Google Sign-Up successful, user:', webResult.user.email);
        }
      } else {
        // Web: Usar popup
        console.log('[SignUpForm] Using popup for Google Sign-Up (web)');
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        const cred = await signInWithPopup(auth, provider);
        uid = cred.user.uid;
      }

      // Verificar si el usuario ya existe
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error(t('forms.usuarionodisponibledespuesdeautenticar'));
      }

      // Verificar si ya existe en Firestore
      const userDocRef = doc(db, "users", uid);
      const userSnapshot = await getDoc(userDocRef);
      const existingUser = userSnapshot.exists() ? userSnapshot.data() : null;

      // Si el usuario ya existe y completó onboarding, redirigir al dashboard
      if (existingUser && existingUser.onboardingCompleted) {
        setError(t('auth.alreadyRegistered'));
        setTimeout(() => {
          navigate("/signin", { 
            replace: true,
            state: { 
              notice: t('auth.accountExistsSignIn')
            }
          });
        }, 2000);
        return;
      }

      // Si el usuario ya existe pero NO completó onboarding, continuar al onboarding
      // Si es nuevo, crear el documento
      if (!existingUser) {
        try {
          try {
            await i18n.changeLanguage(selectedLanguage);
          } catch {}
          await setDoc(
            userDocRef,
            {
              uid,
              email: currentUser.email,
              name: currentUser.displayName || null,
              role: "admin",
              isOwner: true,
              isAdmin: true,
              language: selectedLanguage,
              createdAt: serverTimestamp(),
              accountId: uid,
            },
            { merge: true }
          );
          await upsertAccountSubscription({ ownerUserId: uid, plan: "free" });
          await tryApplyReferral();
        } catch (err) {
          console.error('[SignUpForm] Error creating user:', err);
        }
      }

      // Marcar que es un registro con Google para poder preguntar si quiere salir
      sessionStorage.setItem("google_signup", "true");
      // Marcar que va a onboarding (evita glitch con RequireAuth)
      sessionStorage.setItem("redirecting_to_onboarding", "true");
      navigate("/onboarding/welcome", { replace: true });
    } catch (e: any) {
      if (
        e?.code === "auth/popup-closed-by-user" ||
        e?.code === "auth/cancelled-popup-request"
      ) {
        setError(null);
      } else {
        setError(t("auth.googleSignInFailed"));
        console.error("Error en Google Sign Up:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError(t("auth.completeEmailAndPassword"));
      return;
    }

    if (!isChecked) {
      setError(t("auth.acceptTerms", { defaultValue: "Debes aceptar los Términos y Condiciones y la Política de Privacidad para continuar" }));
      return;
    }

    setLoading(true);

    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;
      const display = firstName.trim();

      if (firstName) {
        try {
          await updateProfile(cred.user, { displayName: display });
        } catch (_) {
          // no-op si falla actualizar displayName
        }
      }

      try {
        try {
          await i18n.changeLanguage(selectedLanguage);
        } catch {}
        await setDoc(
          doc(db, "users", uid),
          {
            uid,
            email: cred.user.email,
            name: cred.user.displayName || display || null,
            role: "admin",
            isOwner: true,
            isAdmin: true,
            language: selectedLanguage,
            createdAt: serverTimestamp(),
            accountId: uid,
          },
          { merge: true }
        );
        await upsertAccountSubscription({ ownerUserId: uid, plan: "free" });
        await tryApplyReferral();
      } catch (_) {}

      // Marcar que va a onboarding (evita glitch con RequireAuth)
      sessionStorage.setItem("redirecting_to_onboarding", "true");
      navigate("/onboarding/welcome", { replace: true });
    } catch (err: any) {
      const code = err?.code as string | undefined;
      switch (code) {
        case "auth/email-already-in-use":
          setError(t("validation.emailAlreadyInUse"));
          break;
        case "auth/invalid-email":
          setError(t("validation.emailNotValid"));
          break;
        case "auth/weak-password":
          setError(t("validation.weakPassword"));
          break;
        case "auth/invalid-api-key":
          setError(t("validation.invalidApiKey"));
          break;
        default:
          setError(t("auth.signupFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loader durante registro
  if (loading) {
    return <GlobalLoader message={t("auth.signingUp", "Creando cuenta...")} />;
  }
  
  const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';

  return (
    <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar p-6 sm:p-10" style={{ backgroundColor: '#fff5ebff' }}>
      {/* Botón volver solo en web */}
      {!isNative && (
        <div className="w-full max-w-md mx-auto mb-5">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ChevronLeftIcon className="size-5" />
            {t("auth.goBack")}
          </Link>
        </div>
      )}

      <div className="flex flex-col flex-1 w-full max-w-md mx-auto">
        {/* Banner opcional (ej: error de signin) */}
        {banner && <div className="mb-6">{banner}</div>}
        
        <div className="mb-5 sm:mb-8">
          <h1 className="mb-2 font-black text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
            {t("auth.signUp")}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("auth.enterEmailAndPassword")}
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
            {t('common.idioma', { defaultValue: 'Idioma' })}
          </label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value as any)}
            className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white cursor-pointer appearance-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <option value="es">{t('common.espanol', { defaultValue: 'Español' })}</option>
            <option value="en">{t('common.english', { defaultValue: 'English' })}</option>
            <option value="pt">{t('common.portugues', { defaultValue: 'Português' })}</option>
          </select>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full inline-flex items-center justify-center gap-3 py-3 text-sm font-black text-gray-700 transition-all bg-white dark:bg-gray-800 rounded-2xl px-7 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.1)]"
        >
          {/* SVG de Google */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
              fill="#4285F4"
            />
            <path
              d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
              fill="#34A853"
            />
            <path
              d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
              fill="#FBBC05"
            />
            <path
              d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
              fill="#EB4335"
            />
          </svg>
          {t("auth.signUpWithGoogle")}
        </button>

        {(Capacitor.getPlatform() === 'ios' || !(Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios')) && (
          <button
            type="button"
            onClick={handleAppleSignIn}
            className="mt-3 w-full inline-flex items-center justify-center gap-3 py-3 text-sm font-black text-gray-700 transition-all bg-white dark:bg-gray-800 rounded-2xl px-7 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.1)]"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.365 1.43c0 1.14-.415 2.204-1.225 3.13-.986 1.14-2.59 2.02-4.1 1.898-.196-1.366.45-2.74 1.35-3.77.986-1.14 2.7-1.97 3.975-2.02.125.255.0.51.0.762zm4.61 17.108c-.54 1.234-.792 1.78-1.485 2.87-.967 1.52-2.33 3.416-4.02 3.43-1.5.015-1.89-.97-3.93-.955-2.04.015-2.47.973-3.97.958-1.69-.015-2.98-1.74-3.95-3.26-2.72-4.26-3.01-9.25-1.33-11.85 1.19-1.85 3.06-2.94 4.82-2.94 1.79 0 2.92.98 4.4.98 1.44 0 2.32-.98 4.38-.98 1.57 0 3.23.85 4.41 2.32-3.89 2.1-3.26 7.55.655 9.425z" />
            </svg>
            {t('auth.signUpWithApple', { defaultValue: 'Registrarse con Apple' })}
          </button>
        )}

        <div className="relative py-3 sm:py-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
              {t("auth.or")}
            </span>
          </div>
        </div>

        <div className="p-6 rounded-[28px] shadow-[0_10px_30px_-5px_rgba(5,85,144,0.04)] bg-white dark:bg-gray-900">
          <form onSubmit={handleSubmit}>
            <div className="space-y-5">
              {/* Nombre */}
              <div>
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  {t("auth.name")}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  autoComplete="name"
                  placeholder={t("auth.enterYourName")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  {t("auth.email")}
                  <span className="text-error-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  placeholder={t("auth.enterYourEmail")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  {t("auth.password")}
                  <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <input
                    placeholder={t("auth.enterYourPassword")}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                  >
                    {showPassword ? (
                      <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                    ) : (
                      <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                    )}
                  </span>
                </div>
              </div>

              {/* Código de referido (opcional) */}
              <div>
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  🎁 {t("auth.referralCode", { defaultValue: "Código de referido (opcional)" })}
                </label>
                <input
                  type="text"
                  id="referralCode"
                  name="referralCode"
                  placeholder={t("auth.referralCodePlaceholder", { defaultValue: "EJ: SIMON-AB12CD34" })}
                  value={referralCode}
                  onChange={(e) => {
                    const clean = e.target.value.toUpperCase().trim();
                    setReferralCode(clean);
                    if (clean) {
                      localStorage.setItem("pending_referral_code", clean);
                    } else {
                      localStorage.removeItem("pending_referral_code");
                    }
                  }}
                  className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-purple-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 font-mono shadow-sm"
                />
                {referralCode && (
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1 ml-2 font-medium">
                    ✨ {t("auth.referralCodeApplied", { defaultValue: "¡Código de referido detectado y aplicado! Recibirás 20% de descuento." })}
                  </p>
                )}
              </div>

              {/* Checkbox */}
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 pt-0.5">
                  <Checkbox checked={isChecked} onChange={setIsChecked} />
                </div>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  {t('auth.acceptTermsPrefix')}{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-black text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 underline"
                  >
                    {t('auth.termsAndConditions')}
                  </a>
                  {" "}{t('auth.acceptTermsAnd')}{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-black text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 underline"
                  >
                    {t('auth.privacyPolicy')}
                  </a>
                </p>
              </div>

              {error && <div className="text-sm font-semibold text-error-500">{error}</div>}

              {/* Button */}
              <div>
                <button
                  type="submit"
                  className="w-full py-3 px-4 text-sm font-black text-white transition-all rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                  style={{ backgroundColor: '#055590ff' }}
                >
                  {loading ? t("auth.creatingAccount") : t("auth.signUp")}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-center text-gray-700 dark:text-gray-400 sm:text-start">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link
              to="/signin"
              className="font-black text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              {t("auth.signIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
